import ShowcaseStripSkeleton from './ShowcaseStripSkeleton';

/**
 * Placeholder rows while a showcase feed's first fetch is in flight.
 *
 * Exists to hold the SHAPE of the page, not to entertain: the rows are fetched
 * client-side, so the first frame has nothing to show, and without a
 * same-shaped placeholder the layout collapses and then hard-swaps the whole
 * subtree when rows land.
 *
 * It only does that job if the shape is the RIGHT one. The first version drew a
 * `.browse-heading-row` and 40px-wide cards in a plain flex strip — none of
 * which the loaded row uses — so the placeholder and the content it stood in for
 * had no dimension in common. This mirrors `components/showcase.tsx` element for
 * element instead:
 *
 *   div.mb-8                              20px below each row
 *     div.flex.items-center.mb-4          10px below the heading
 *       h2 at 3rem                        the row title
 *     div.relative > .listing-slider      the card strip
 *       .product-list-item                300px wide (185 on mobile), 10px gap
 *
 * The wrapper classes are the loaded row's own, copied verbatim rather than
 * converted, so they cannot drift from it — `mb-8` means the same 32px on both
 * sides of the swap now that Tailwind's scale is absolute pixels app-wide (the
 * `:root` block in styles/tailwind.css). `skeleton-scope` on the bars is left as
 * a marker; it used to carry that re-basing for the bars alone, back when the
 * scale was scoped. The card widths come from `.product-list-item` itself.
 */

interface ShowcaseRowSkeletonProps {
  /** How many placeholder rows, matching the first page size. */
  rows?: number;
  /** Cards per row. Six covers the widest viewport that shows a full strip. */
  cards?: number;
}

export function ShowcaseRowSkeleton({ rows = 4, cards = 6 }: ShowcaseRowSkeletonProps) {
  return (
    <div data-testid="showcase-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div className="mb-8" key={`showcase-skeleton-row-${rowIndex}`}>
          <div className="showcase-heading-row">
            <h2 className="showcase-heading">
              {/* A non-breaking space gives the bar the h2's own line box, so
                  the heading is exactly as tall as real text. Block, not
                  inline-block: an inline-block sits on the baseline and adds
                  its descender to the line, which made the row 0.9px taller
                  than the loaded one. */}
              <div className="skeleton skeleton-scope rounded-md block w-48">
                &nbsp;
              </div>
            </h2>
          </div>
          {/* Flush, because the rows this stands in for are `<Showcase>`s
              that take the default `noMargin = true` — a slider with no
              `slidesOffsetBefore`. The product page's two rows are the ones
              that pass `noMargin={false}`, and they use ShowcaseStripSkeleton
              directly with its inset default. */}
          <ShowcaseStripSkeleton cards={cards} flush />
        </div>
      ))}
    </div>
  );
}

export default ShowcaseRowSkeleton;
